import { Mail, Paperclip } from 'lucide-react';
import { senderDisplayName } from '@/lib/sender';

export interface SourceInfoProps {
  provider?: string;
  sender: string;
  attachmentCount?: number;
  className?: string;
  /**
   * The message reached us because someone relayed it, not because the sender
   * wrote to us. Review used to hide this, which is how a receipt a parent
   * forwarded themselves appeared under a company nobody had approved
   * (YAR-427). Nothing about who is let in changed — only what we say.
   */
  isForwarded?: boolean;
  /**
   * Who relayed it. Null for anything ingested before we started recording it:
   * the outer `From` was never stored and cannot be recovered. Those rows
   * still say "Forwarded", which is the part that was actually misleading.
   */
  forwardedBy?: string | null;
  /**
   * The school, gym or club the message is really from, when it arrived
   * through a shared sending platform (YAR-427).
   *
   * Shown as context under the sender rather than in place of it. The From
   * display name is frequently right — PushPress puts the business there —
   * and replacing a real name with a bare domain would be a downgrade. What
   * was missing is the case where the platform puts a staff member's name
   * there instead, and a parent sees a stranger on their own child's mail.
   *
   * Null whenever ingestion could not recover it, which includes every email
   * stored before this shipped: it is computed from Reply-To and List-Id, and
   * neither is kept on the row.
   */
  senderOrg?: string | null;
}

/**
 * Whether the sender's display name already names the organisation.
 *
 * "Forj LA" against `forjla.com` is the same answer twice, and a second line
 * repeating the first is noise on a card that is already dense. Deliberately
 * conservative: it only suppresses on a real containment match, so an
 * uncertain case shows the extra line rather than hiding information.
 */
function nameAlreadySays(displayName: string, org: string): boolean {
  const normalise = (v: string) =>
    v
      .toLowerCase()
      .replace(/^www\./, '')
      .replace(/[^a-z0-9]/g, '');
  const name = normalise(displayName);
  const domain = normalise(org.split('.')[0] ?? org);
  if (!name || !domain) return false;
  return name.includes(domain) || domain.includes(name);
}

export function SourceInfo({
  provider = 'Gmail',
  sender,
  attachmentCount = 0,
  className = '',
  isForwarded = false,
  forwardedBy = null,
  senderOrg = null,
}: SourceInfoProps) {
  // When we know the relayer, THEY are the sender: they are who put this in
  // front of the household. The school moves to a second line as context, so
  // no information is lost — it just stops claiming to have written to us.
  const primary = isForwarded && forwardedBy ? forwardedBy : sender;
  const original = isForwarded && forwardedBy ? sender : null;
  return (
    <div
      // `min-w-0` is load-bearing. This sits in a `flex justify-between` row
      // next to a `shrink-0` "Review N items" chip, and a flex item defaults
      // to `min-width: auto` — it will not shrink below the intrinsic width of
      // its content. A long unbreakable sender like
      // `no-reply-customerservice@blackbaud.school` therefore refused to
      // shrink and pushed the chip off the right edge of the card.
      className={['flex min-w-0 flex-col gap-0.5', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center gap-1.5">
        <Mail
          size={12}
          className="text-muted-foreground"
        />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Via {provider}
          {isForwarded && ' · Forwarded'}
        </span>
      </div>
      {/* Show the sender's display name, not the raw address (YAR-131). The
          stored `from_email` is the verbatim From header, so it often carries
          a `"Name" <addr>` pair; the raw value stays available on hover. */}
      <span
        className="truncate text-sm font-semibold text-text-body"
        title={primary}
      >
        {senderDisplayName(primary)}
      </span>
      {original && (
        <span
          className="truncate text-xs text-muted-foreground"
          title={original}
        >
          Originally from {senderDisplayName(original)}
        </span>
      )}
      {/* Suppressed when the display name already says it, so a card does not
          read "Forj LA / via Forj LA". Compared case-insensitively and without
          the leading `www.`, which is how the same organisation differs
          between a display name and a domain. */}
      {senderOrg && !nameAlreadySays(senderDisplayName(primary), senderOrg) && (
        <span
          className="truncate text-xs text-muted-foreground"
          title={senderOrg}
        >
          via {senderOrg}
        </span>
      )}
      {attachmentCount > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Paperclip size={12} />
          {attachmentCount} attachment{attachmentCount > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}
